// ============================================================================
// CLOSESPRINTMODAL.JS — Modal "Terminar sprint"
// ============================================================================
// Sustituye al confirm() de texto plano al cerrar el sprint activo. Muestra
// cuántas tareas se cierran (validadas por SEO) vs cuántas quedan sin cerrar,
// y deja elegir a dónde van las sin-cerrar: Backlog, el sprint de planificación
// (si existe) o un sprint nuevo (que se crea en planificación). El padre hace
// la llamada al store (closeSprint) + toasts; aquí solo se recoge la decisión.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { List, Play, Plus, X } from 'lucide-react';
import '../styles/CloseSprintModal.css';

function CloseSprintModal({ sprint, sprintTasks = [], planningSprint = null, onCancel, onConfirm }) {
  // "Cerrada" = validadoPorSEO (terminada Y validada por SEO). Las demás quedan
  // "sin cerrar" y son las que se reparten. Mismo criterio que el backend.
  const { cerradas, sinCerrar } = useMemo(() => {
    const c = sprintTasks.filter(t => !!t.validadoPorSEO).length;
    return { cerradas: c, sinCerrar: sprintTasks.length - c };
  }, [sprintTasks]);

  // Destino por defecto: si hay sprint de planificación, mover ahí; si no, backlog.
  const [destino, setDestino] = useState(planningSprint ? 'sprint' : 'backlog');
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoInicio, setNuevoInicio] = useState('');
  const [nuevoFin, setNuevoFin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const boxRef = useRef(null);

  // Accesibilidad del diálogo: foco inicial en el primer control y cierre con
  // Escape (mismo guard que el overlay: no se cierra mientras se envía).
  useEffect(() => {
    const first = boxRef.current?.querySelector('.csm-body button, .csm-body input, .csm-foot button');
    if (first) first.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [submitting, onCancel]);

  const nuevoValido =
    nuevoNombre.trim() !== '' &&
    nuevoInicio !== '' &&
    nuevoFin !== '' &&
    new Date(nuevoFin) > new Date(nuevoInicio);

  const confirmDisabled = submitting || (sinCerrar > 0 && destino === 'nuevo' && !nuevoValido);

  const handleConfirm = async () => {
    let opts = {};
    if (sinCerrar > 0) {
      if (destino === 'sprint' && planningSprint) {
        opts = { targetSprintId: planningSprint.id };
      } else if (destino === 'nuevo') {
        opts = {
          nuevoSprint: {
            nombre: nuevoNombre.trim(),
            fecha_inicio: nuevoInicio,
            fecha_fin: nuevoFin,
          },
        };
      }
    }
    // Sin tareas sin cerrar → destino 'backlog' (no mueve nada).
    const finalDestino = sinCerrar > 0 ? destino : 'backlog';

    setSubmitting(true);
    const result = await onConfirm(finalDestino, opts);
    // Éxito → el padre desmonta el modal. Fallo → reactivar para reintentar.
    if (!result || result.success !== true) {
      setSubmitting(false);
    }
  };

  return (
    <div className="sprint-modal-overlay u-sheet-overlay" onClick={submitting ? undefined : onCancel}>
      <div
        ref={boxRef}
        className="close-sprint-modal u-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="csm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="csm-head">
          <h3 className="csm-title" id="csm-title">Terminar sprint</h3>
          {/* Cierre de 44 px solo en teléfono (hoja inferior); en el resto de
              anchos el modal se cierra con Cancelar, el overlay o Escape. */}
          <button
            type="button"
            className="csm-close u-only-phone"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="csm-body">
          <p className="csm-lead">
            El sprint <span className="csm-sname">{sprint?.nombre}</span> contiene:
          </p>

          <div className="csm-tally">
            <div className="csm-ti">
              <span className="csm-dot csm-dot-ok" />
              <b>{cerradas}</b> terminadas y validadas por SEO
              <span className="csm-muted">→ se cierran</span>
            </div>
            <div className="csm-ti">
              <span className="csm-dot csm-dot-warn" />
              <b>{sinCerrar}</b> sin cerrar
              <span className="csm-muted">(no terminadas o sin validar SEO)</span>
            </div>
          </div>

          {sinCerrar > 0 && (
            <>
              <p className="csm-fieldlbl">Mover las tareas sin cerrar a</p>
              <div className="csm-select">
                <button
                  type="button"
                  className={`csm-opt ${destino === 'backlog' ? 'sel' : ''}`}
                  onClick={() => setDestino('backlog')}
                >
                  <List size={15} className="csm-oi" />
                  Backlog
                </button>

                {planningSprint && (
                  <button
                    type="button"
                    className={`csm-opt ${destino === 'sprint' ? 'sel' : ''}`}
                    onClick={() => setDestino('sprint')}
                  >
                    <Play size={15} className="csm-oi" />
                    {planningSprint.nombre}
                    <span className="csm-newtag">Planificación</span>
                  </button>
                )}

                {/* Sólo puede existir 1 sprint de planificación por cliente: si ya
                    hay uno, no se ofrece "crear nuevo" (el backend lo rechazaría). */}
                {!planningSprint && (
                  <button
                    type="button"
                    className={`csm-opt ${destino === 'nuevo' ? 'sel' : ''}`}
                    onClick={() => setDestino('nuevo')}
                  >
                    <Plus size={15} className="csm-oi" />
                    Crear nuevo sprint…
                  </button>
                )}
              </div>

              {destino === 'nuevo' && (
                <div className="csm-newform">
                  <div className="csm-field">
                    <label className="csm-lbl">Nombre del sprint</label>
                    <input
                      type="text"
                      className="csm-input"
                      value={nuevoNombre}
                      onChange={(e) => setNuevoNombre(e.target.value)}
                      placeholder="Ej: WEB.26_07.SEO-SP20"
                      autoFocus
                    />
                  </div>
                  <div className="csm-field-row">
                    <div className="csm-field">
                      <label className="csm-lbl">Inicio</label>
                      <input
                        type="date"
                        className="csm-input"
                        value={nuevoInicio}
                        onChange={(e) => setNuevoInicio(e.target.value)}
                      />
                    </div>
                    <div className="csm-field">
                      <label className="csm-lbl">Fin</label>
                      <input
                        type="date"
                        className="csm-input"
                        value={nuevoFin}
                        onChange={(e) => setNuevoFin(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="csm-foot">
          <button
            type="button"
            className="csm-btn csm-btn-ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="csm-btn csm-btn-danger"
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            {submitting ? 'Terminando…' : 'Terminar sprint'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CloseSprintModal;
